/**
 * Voice Platform env — global `ENV` from Rails layouts (same pattern as WEBSOCKET_URL).
 */

let voiceApiUrlNormalizeWarned = false;

/**
 * @param {string} raw
 * @returns {string}
 */
function normalizeVoicePlatformApiBaseUrl(raw) {
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return '';
  }

  const withoutQueryOrHash = trimmed.split('?')[0].split('#')[0];
  let adjusted = /\/+$/.test(withoutQueryOrHash);

  try {
    const parsed = new URL(trimmed);
    parsed.search = '';
    parsed.hash = '';

    let pathname = parsed.pathname.replace(/\/+$/, '') || '';

    if (pathname === 'voice' || pathname === '/voice') {
      pathname = '';
      adjusted = true;
    } else if (pathname.endsWith('/voice')) {
      pathname = pathname.slice(0, -'/voice'.length).replace(/\/+$/, '');
      adjusted = true;
    }

    const baseUrl = pathname
      ? `${parsed.origin}/${pathname.replace(/^\/+/, '')}`
      : parsed.origin;

    if (adjusted && !voiceApiUrlNormalizeWarned) {
      voiceApiUrlNormalizeWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[Voice] VOICE_PLATFORM_API_URL was normalized.',
        { configured: trimmed, using: baseUrl },
        'Use the HTTP base without trailing slash or /voice (socket uses …/voice).'
      );
    }

    return baseUrl;
  } catch {
    let baseUrl = trimmed.replace(/\/+$/, '');
    if (/\/voice$/i.test(baseUrl)) {
      baseUrl = baseUrl.replace(/\/voice$/i, '').replace(/\/+$/, '');
      adjusted = true;
    }

    if (adjusted && !voiceApiUrlNormalizeWarned) {
      voiceApiUrlNormalizeWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[Voice] VOICE_PLATFORM_API_URL was normalized.',
        { configured: trimmed, using: baseUrl },
        'Use the HTTP base without trailing slash or /voice.'
      );
    }

    return baseUrl;
  }
}

/**
 * HTTP base URL for voice-service (no trailing slash).
 *
 * @returns {string}
 */
export function getVoicePlatformApiBaseUrl() {
  if (typeof ENV === 'undefined' || !ENV.VOICE_PLATFORM_API_URL) {
    return '';
  }

  return normalizeVoicePlatformApiBaseUrl(ENV.VOICE_PLATFORM_API_URL);
}

/**
 * Socket.IO namespace URL for voice-service (`/voice`). Never the notifications socket.
 *
 * Prefers `ENV.VOICE_PLATFORM_SOCKET_URL` (e.g. https://voice-app.mercately.com/voice).
 * Falls back to `{VOICE_PLATFORM_API_URL}/voice`.
 *
 * @returns {string}
 */
export function getVoicePlatformSocketUrl() {
  if (typeof ENV !== 'undefined' && ENV.VOICE_PLATFORM_SOCKET_URL) {
    return normalizeVoicePlatformSocketUrl(ENV.VOICE_PLATFORM_SOCKET_URL);
  }

  const baseUrl = getVoicePlatformApiBaseUrl();
  if (!baseUrl) {
    return '';
  }

  try {
    const parsed = new URL(baseUrl);
    parsed.pathname = '/voice';
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
}

/**
 * @param {string} raw
 * @returns {string}
 */
function normalizeVoicePlatformSocketUrl(raw) {
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return '';
  }

  try {
    const parsed = new URL(trimmed);
    parsed.search = '';
    parsed.hash = '';
    const pathname = parsed.pathname.replace(/\/+$/, '') || '';
    if (!pathname || pathname === '/') {
      parsed.pathname = '/voice';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return trimmed.replace(/\/+$/, '');
  }
}

/**
 * Voice room is the retailer id as a string (not retailer_user_id / notifications room).
 *
 * @returns {string}
 */
export function getCurrentVoiceRetailerRoomId() {
  if (typeof window !== 'undefined' && window.__MERCATELY_RETAILER_INFO__?.id != null) {
    return String(window.__MERCATELY_RETAILER_INFO__.id);
  }
  if (typeof ENV !== 'undefined' && ENV.CURRENT_RETAILER_ID) {
    return String(ENV.CURRENT_RETAILER_ID);
  }
  return '';
}

/**
 * Agent identity for voice-service (join_room + HTTP). Not the Socket.IO room.
 *
 * @returns {string}
 */
export function getCurrentVoiceRetailerUserId() {
  if (typeof ENV !== 'undefined' && ENV.CURRENT_RETAILER_USER_ID) {
    return String(ENV.CURRENT_RETAILER_USER_ID);
  }
  return '';
}

/**
 * Socket + HTTP API base configured (required to mount Voice runtime).
 *
 * @returns {boolean}
 */
export function isVoicePlatformRuntimeConfigured() {
  return Boolean(getVoicePlatformApiBaseUrl());
}
